import {DocumentsGridRawRow} from './documents';
import {PackageTypes} from './gtins';
import {CodeResponseTypeInfo} from './validation';

export type ExistingInvoiceState = string;
export type NewInvoiceIdState = 'new';

export interface PageValues {
    periodFrom: string;
    periodTo: string;
    taxPointDate: string;
    buyerReference: string;
    contractDocumentReference: string;
    purchaseOrderReference: string;
    salesOrderReference: string;
}

export interface InfoValues {
    bic?: string;
    companyNameAndTIN: string;
    companyTIN: string;
    customerAddress?: string;
    customerCompanyNameOrTIN:
        | {value: string; label: string; postalCode?: string; city?: string}
        | undefined;
    customerEmail?: string;
    customerPhoneNumber?: string;
    dueDate: Date | string;
    iban?: string;
    irn: string;
    issueDate: Date | string;
    storageLocationAddress?: string;
    storageLocationName: {value: string; label: string} | string;
    supplierStreetName?: string;
    supplierCityName?: string;
    supplierPostalZone?: string;
    supplierCountry?: string;
    supplierEmail?: string;
    supplierPhoneNumber?: string;
    customerZip: string;
    customerCity: string;
    customerCountry: string;
    customerStreet: string;
}

export interface InvoiceLine {
    description: string;
    code?: string;
    gtin?: string;
    isHsn?: 'HS' | 'MP';
    serviceCategory?: {label: string; value: string};
    qty: string;
    price: string;
    lines: string;
    vatCategory: {label: string; value: string} | null;
    vat: string;
    lineAmountWVat: string;
    charges?: InvoiceLineChargeData;
    // rate: string;
    // amount: string;
    default?: boolean;
    copyActions?: {
        allowToCopy: boolean;
        allowToDuplicateDown: boolean;
        allowToPaste: boolean;
        allowToDelete: boolean;
    };
    isMarkedItem: boolean;
    combinedCodes?: string[];
    combinedCodeId?: string;
    packagingType?: PackageTypes;
    isCombined?: boolean;
    level?: number;
    parent?: string;
    sellersItemId?: string;
    buyersItemId?: string;
}

export interface InvocieLineHolder {
    description: string;
    code: string;
    gtin: string;
    isHsn?: 'HS' | 'MP';
    serviceCategory?: {label: string; value: string};
    qty?: string;
    price?: string;
    lines?: string;
    vatCategory?: {label: string; value: string};
    vat?: string;
    lineAmountWVat?: string;
    charges?: InvoiceLineChargeData;
    copyActions?: {
        allowToCopy: boolean;
        allowToDuplicateDown: boolean;
        allowToPaste: boolean;
        allowToDelete: boolean;
    };
    isCombined?: boolean;
    isMarkedItem: boolean;
    combinedCodes?: string[];
    combinedCodeId?: string;
    packagingType?: PackageTypes;
    level?: number;
    parent?: string;
    childrenMarkingCodes?: CodeResponseTypeInfo[];
    subRows?: InvocieLineHolder[];
    __rowId?: string;
    sellersItemId?: string;
    buyersItemId?: string;
}

export interface InvoiceLineChargeData {
    discountRateAndBaseAmount: boolean;
    isCharge: 'true' | 'false';
    reason: string;
    amountXaf: number;
    ratePercent?: number;
    baseAmount?: number;
}

export interface AllowanceCharge {
    isCharge: 'charge' | 'allowance';
    isRatePercent: boolean;
    reason: string;
    rate?: string;
    baseAmount?: string;
    totalAmount: string;
    vatCategory: {label: string; value: string} | null;
    vat: string;
}

export type TaxesAndTotalAllowanceCharge = Omit<AllowanceCharge, 'isCharge' | 'isRatePercent'> & {
    mutualIndex: number;
};

export type RoundAndPrepayd = {
    name: 'prepaidAmount' | 'payableRoundingAmount';
    value: string | number;
};

export interface TaxesAndTotal {
    calculationsByTaxCategory: {
        taxCategory: string | number;
        rate: string | number;
        taxableAmount: string | number;
        taxAmount: string | number;

        taxCategoryDisplay?: string | number;
        rateDisplay?: string | number;
        taxableAmountDisplay?: string | number;
        taxAmountDisplay?: string | number;
        taxExemptionReason?: string;
    }[];
    taxTotal: string;
    // chargesRadio: string;
    // charges: {id?: string; charge: string; value: string}[];
    // whtAppliedRadio_2: string;
    baseAmount: string;
    multiplierFactorNumeric: string;
    whtChargeAmount_1: string;
    lineExtensionAmount: string;
    taxExclusiveAmount: string;
    taxExclusiveAmountDisplay?: string;
    taxInclusiveAmount: string;
    taxInclusiveAmountDisplay?: string;
    // whtChargeAmount_2: string;
    prepaidAmount: string;
    payableRoundingAmount: string;
    payableAmount: string;
    payableAmountDisplay?: string;
    // paymentDueDate: string;
    allowanceCharge?: AllowanceCharge[];
    allowanceTotalAmount: string;
    allowanceTotalAmountDisplay?: string;
    chargeTotalAmount: string;
    chargeTotalAmountDisplay?: string;
    withholdingTax: string;
}
export interface TaxesFullObj {
    data: TaxesAndTotal;
    allowToSend: boolean;
}

export interface DocumentsGridRawRowExtended extends DocumentsGridRawRow {
    side: 'OUTGOING' | 'INCOMING';
}

export interface InvoiceFiltersState {
    aggregateparentcode: [string] | null;
    buyer: [string] | null;
    buyerLocation: {
        storage: 'string';
        address: 'string';
    }[];
    createdat: [string] | null;
    owner: [string] | null;
    seller: [string] | null;
    sellerLocation: {
        storage: 'string';
        address: 'string';
    }[];
    side: ['INCOMING' | 'OUTGOING' | 'MOVING'];
    status: [string] | null;
    type: [string] | null;
}

export type InvoiceLineErrorEntry = {index: string; fields: string[]};

export enum LockInvoiceCodesReason {
    QUALITY_AND_PHYSICAL_CONDITION = 'QUALITY_AND_PHYSICAL_CONDITION',
    REGULATORY_AND_LEGAL_RESTRICTIONS = 'REGULATORY_AND_LEGAL_RESTRICTIONS',
    INVESTIGATION_AND_CONTROL = 'INVESTIGATION_AND_CONTROL',
    LOGISTICS_AND_OPERATIONAL_RESTRICTIONS = 'LOGISTICS_AND_OPERATIONAL_RESTRICTIONS',
    FINANCIAL_AND_COMMERCIAL_RESTRICTIONS = 'FINANCIAL_AND_COMMERCIAL_RESTRICTIONS',
    RETURNS_AND_POST_SALE_PROCESSING = 'RETURNS_AND_POST_SALE_PROCESSING',
    TECHNICAL_AND_DATA_ISSUES = 'TECHNICAL_AND_DATA_ISSUES',
}

export enum LockInvoiceCodesScope {
    AGGREGATION = 'AGGREGATION',
    INTRODUCTION = 'INTRODUCTION',
    INVOICING = 'INVOICING',
    RETAIL = 'RETAIL',
    WRITE_OFF = 'WRITE_OFF',
    MOVING = 'MOVING',
    ALL = 'ALL',
}

export enum LockInvoiceCodesSource {
    INTERNAL = 'INTERNAL',
    REGULATOR = 'REGULATOR',
    SUPPLIER = 'SUPPLIER',
    CUSTOMER = 'CUSTOMER',
    LOGISTICS_PROVIDER = 'LOGISTICS_PROVIDER',
    CUSTOMS_AUTHORITY = 'CUSTOMS_AUTHORITY',
    EXTERNAL_SYSTEM = 'EXTERNAL_SYSTEM',
    MANUAL_USE = 'MANUAL_USE',
}

export interface HandleLinesForChangeToLockStatus {
    identityCode: string;
    lockReasons: LockInvoiceCodesReason[];
    lockScopes: LockInvoiceCodesScope[];
    lockSources: LockInvoiceCodesSource[];
}
